import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { SharedModule } from '../../../../../@commons/ui-components/shared.module';
import { BaseToastComponent } from '../../../../../@commons/ui-components/toast-popup/base-toast-component';
import { Address } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/address.entity';
import { LocationInputComponent } from '../../../../../@commons/ui-components/location-input/location-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AddressApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address/address-api';
import { UpdateAddressRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/update-address.request';

/**
 * Changer l'adresse d'un potager, ouverte depuis sa fiche. Elle se ferme sur
 * `true` une fois l'adresse enregistrée, pour que la fiche la relise.
 */
@Component({
  selector: 'vw-edit-user-adress-toast',
  imports: [SharedModule, LocationInputComponent, TranslatePipe],
  templateUrl: './edit-user-adress-toast.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: AddressApi,
      useClass: AddressApi,
    },
  ],
})
export class EditUserAdressToastComponent extends BaseToastComponent implements OnChanges {
  @Input() address: Address;

  addressInput: string = '';
  selectedFeature: any;
  isUpdatingAddress = false;
  /** L'enregistrement a échoué : la feuille reste ouverte et le dit. */
  saveFailed = false;
  /** « Enregistrer » touché sans suggestion choisie : la feuille dit quoi faire. */
  pickMissing = false;

  constructor(private addressApi: AddressApi) {
    super();
  }

  /** Chaque ouverture repart de l'adresse enregistrée : une saisie abandonnée ne reste pas. */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && this.isOpen) {
      this.addressInput = this.currentAddress;
      this.selectedFeature = undefined;
      this.saveFailed = false;
      this.pickMissing = false;
    }
  }

  /** L'adresse complète si on la connaît, sinon ses morceaux, sans « undefined ». */
  get currentAddress(): string {
    const a = this.address;
    if (!a) return '';
    if (a.fullAddress) return a.fullAddress;
    return [a.line1, a.line2, a.postalCode, a.city].filter(Boolean).join(' ');
  }

  async onSelectFeature(feature: any) {
    this.selectedFeature = feature;
    this.addressInput = feature.place_name;
    this.pickMissing = false;
  }
  getValueFromFeature(
    feature: any,
    key: string,
    targetVal: string = 'text',
  ): string | undefined {
    let result: string | undefined = undefined;

    if (feature.id && feature.id.indexOf(key) >= 0) {
      result = feature[targetVal];
    } else if (
      feature.context.some((context: any) => context.id.indexOf(key) >= 0)
    ) {
      result = feature.context.find(
        (context: any) => context.id.indexOf(key) >= 0,
      )[targetVal];
    }

    return result;
  }

  async onSaveAddress() {
    // Rien à enregistrer tant qu'aucune suggestion n'est choisie : on le dit.
    if (!this.selectedFeature) {
      this.pickMissing = true;
      return;
    }
    this.isUpdatingAddress = true;
    this.saveFailed = false;
    const updateAddressRequest: UpdateAddressRequest =
      new UpdateAddressRequest();
    updateAddressRequest.uid = this.address.uid;
    if (this.selectedFeature.context) {
      updateAddressRequest.city = this.getValueFromFeature(
        this.selectedFeature,
        'municipality',
      );
      updateAddressRequest.postalCode = this.getValueFromFeature(
        this.selectedFeature,
        'postal_code',
      );
      updateAddressRequest.countryCode = this.getValueFromFeature(
        this.selectedFeature,
        'country',
        'country_code',
      );
      updateAddressRequest.country = this.getValueFromFeature(
        this.selectedFeature,
        'country',
      );
      updateAddressRequest.line1 = this.selectedFeature.place_name;
      updateAddressRequest.fullAddress = this.selectedFeature.place_name;

      if (this.selectedFeature.center) {
        updateAddressRequest.latitude = this.selectedFeature.center[1];
        updateAddressRequest.longitude = this.selectedFeature.center[0];
      }

      updateAddressRequest.address = this.selectedFeature.place_name;
      try {
        await this.addressApi.update(updateAddressRequest);
      } catch (e) {
        // Mise à jour refusée : la feuille reste ouverte, le dit, et on peut réessayer.
        console.error('EditUserAdressToastComponent: failed to update address', e);
        this.isUpdatingAddress = false;
        this.saveFailed = true;
        return;
      }
    }
    this.isUpdatingAddress = false;
    // Une suggestion sans contexte n'a rien enregistré : la fiche n'a rien à relire.
    this.onClose(!!this.selectedFeature.context);
  }
}
